import { Trans, useLingui } from '@lingui/react/macro';
import { t } from '@lingui/macro';
import { Button, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'src/components/ui';
import { useOpenModal } from '../FormModal';
import { UWEEntity } from '../../api/types';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { useContext, useEffect, useState } from 'react';
import UWEEntityApi from '../../api/UWEEntityApi';
import Loader from '../Loader';
import OcifExtensionsContext from '../../context/OcifExtensionsContext';
import { useDarkMode } from '../../context/DarkModeContext';
import { getTestId } from '../../hooks/useTestId';
import Icon from 'src/components/Icon';

dayjs.extend(localizedFormat);

const PARAMS = ['rowObject', 'setStatus'];

const periodTypes: Record<string, { label: string; dataKey: string }> = {
  monthly: {
    label: t`Monthly`,
    dataKey: 'month',
  },
  quarterly: {
    label: t`Quarterly`,
    dataKey: 'quarter',
  },
  yearly: {
    label: t`Yearly`,
    dataKey: 'year',
  },
  weekly: {
    label: t`Weekly`,
    dataKey: 'week',
  },
};

type SubmitResult = { status: 'success'; newDueDate: string } | { status: 'already_extended' } | { status: 'max_extensions_reached' };

const RequestExtensionModal = ({
  rowObject,
  setStatus,
  onConfirm: onClose,
  isOpen,
}: {
  rowObject: UWEEntity;
  setStatus: Function;
  onConfirm: Function;
  isOpen: boolean;
}) => {
  const { i18n } = useLingui();
  const { isDarkMode } = useDarkMode();

  const extensionsContext = useContext(OcifExtensionsContext);
  const { setFetchExtensions = () => {} } = extensionsContext || {};

  const [justification, setJustification] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [result, setResult] = useState<SubmitResult | null>(null);

  const windowPeriodType = rowObject?.metadata?.reportingWindowPeriodType;
  const periodType = periodTypes[windowPeriodType] || {};

  const ptLabel = periodType.label;
  const ptKey = periodType.dataKey;

  let periodId = '';
  switch (windowPeriodType) {
    case 'yearly':
      periodId = rowObject?.metadata?.[ptKey];
      break;
    case 'monthly':
      periodId = dayjs()
        .month(Number(rowObject?.metadata?.[ptKey]) - 1) // 0 indexed
        .year(Number(rowObject?.metadata?.year))
        .locale(i18n.locale)
        .format('MMMM YYYY');
      break;
    case 'quarterly':
      periodId = `${rowObject?.metadata?.[ptKey]} ${rowObject?.metadata?.year}`;
      break;
    case 'weekly': {
      const week = rowObject?.metadata?.[ptKey];
      const year = rowObject?.metadata?.year;
      periodId = t`Week ${week} ${year}`;
      break;
    }
    default:
  }

  let submissionPeriod = ptLabel ? ptLabel : '';
  submissionPeriod = ptKey && rowObject?.metadata?.[ptKey] ? `${submissionPeriod} - ${periodId}` : submissionPeriod;

  const newDueDate = dayjs(rowObject?.metadata?.dueDate)
    .add(15, 'days')
    .locale(i18n.locale)
    .format('LL');

  const handleConfirmRequest = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const workflow = await UWEEntityApi.getWorkflowInfoForSlug({ slug: 'extension-request' });
      const wfAccessToken = await UWEEntityApi.createWfAccessTokenHash({
        workflowId: workflow.id,
        canView: true,
        canCreate: true,
        canUpdate: true,
      });

      const newExtensionReq = await UWEEntityApi.createUWEEntity({
        page: 'extension-request-form-create',
        formData: {
          action: 'submit',
          comments: [],
          entityTypeId: workflow.objectSchemaId,
          data: { reportId: rowObject.id, justification },
          organizationalUnit: { id: rowObject.organizationalUnit.id },
        },
        wfAccessToken,
      });

      const entityAccessToken = await UWEEntityApi.createWfAccessTokenHash({
        workflowId: workflow.id,
        uweEntityId: newExtensionReq.data.id,
        canView: true,
        canCreate: false,
        canUpdate: false,
      });

      const extensionReq = await UWEEntityApi.getUWEEntity({
        entityId: newExtensionReq?.data?.id,
        wfAccessToken: entityAccessToken,
      });

      const { reportingWindowExtensions, extensionsThisYear } = extensionReq?.data ?? {};

      if (!reportingWindowExtensions) {
        const status = 'already_extended';
        setResult({ status });
        setStatus(status);
      } else if (extensionsThisYear) {
        const status = 'max_extensions_reached';
        setResult({ status });
        setStatus(status);
      } else {
        const status = 'success';
        setResult({ status, newDueDate });
        setStatus(status);
        setFetchExtensions(true, true);
      }
    } catch (e) {
      setError(e as Error);
    }

    setLoading(false);
  };

  const handleClose = () => {
    setResult(null);
    setError(null);
    setJustification('');
    onClose();
  };

  const isSuccess = result?.status === 'success';
  const isDenied = result?.status === 'already_extended' || result?.status === 'max_extensions_reached';

  return (
    <Modal isOpen={isOpen} toggle={handleClose} className="modal-primary" size="md" backdrop>
      {loading && (
        <div
          style={{
            background: isDarkMode ? 'rgba(34,36,42,0.9)' : 'rgba(255,255,255,0.9)',
            zIndex: 20,
            position: 'absolute',
            width: '100%',
            height: '100%',
            top: 0,
            right: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Loader>
            <Trans>Verifying extension request...</Trans>
          </Loader>
        </div>
      )}

      <ModalHeader toggle={handleClose}>
        <Trans>Request Extension</Trans>
      </ModalHeader>

      <ModalBody>
        {/* ── Success state ── */}
        {isSuccess && (
          <div className="text-center py-3" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem' }}>
            <Icon icon="CheckCircle" className="text-success" style={{ fontSize: '3rem' }} />
            <h5 className="text-success mb-0">
              <Trans>Extension Granted!</Trans>
            </h5>
            <p className="mb-0 px-2">
              <Trans>Your extension request has been approved.</Trans>
              <br />
              <Trans>Your new due date is:</Trans>
            </p>
            <p className="text-primary font-bold mb-0" style={{ fontSize: '1.1rem' }}>
              {newDueDate}
            </p>
            <small className="text-muted">
              <Trans>(+15 calendar days from the original due date)</Trans>
            </small>
          </div>
        )}

        {/* ── Denial: already has an active extension for this report ── */}
        {result?.status === 'already_extended' && (
          <div className="text-center py-3" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem' }}>
            <Icon icon="XCircle" className="text-warning" style={{ fontSize: '3rem' }} />
            <h5 className="text-warning mb-0">
              <Trans>Extension Already Submitted</Trans>
            </h5>
            <p className="mb-0 px-2">
              <Trans>An extension has already been submitted for this report.</Trans>
              <br />
              <Trans>Only one active extension is allowed per reporting window.</Trans>
            </p>
          </div>
        )}

        {/* ── Denial: hit the yearly extension cap ── */}
        {result?.status === 'max_extensions_reached' && (
          <div className="text-center py-3" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem' }}>
            <Icon icon="XCircle" className="text-danger" style={{ fontSize: '3rem' }} />
            <h5 className="text-danger mb-0">
              <Trans>Extension Limit Reached</Trans>
            </h5>
            <p className="mb-0 px-2">
              <Trans>You have already used 2 extensions this year.</Trans>
              <br />
              <Trans>No further extensions can be granted for the current reporting year.</Trans>
            </p>
          </div>
        )}

        {/* ── Default form state ── */}
        {!result && (
          <>
            <div>
              <Trans>You are requesting an extension for report</Trans>{' '}
              <span className="font-bold">{rowObject?.metadata?.reportingWindowPeriod}</span>
            </div>
            <div className="mt-3">
              <Trans>Institution:</Trans> <span className="font-bold">{rowObject?.organizationalUnit?.name}</span>
            </div>
            {submissionPeriod && (
              <div className="mt-1" style={{ textTransform: 'capitalize' }}>
                <Trans>Submission Period:</Trans> <span className="font-bold">{submissionPeriod}</span>
              </div>
            )}
            <div className="mt-1">
              <Trans>Current due date:</Trans>{' '}
              <span className="font-bold">
                {dayjs(rowObject?.metadata?.dueDate)
                  .locale(i18n.locale)
                  .format('LL')}
              </span>
            </div>
            <div className="mt-3">
              <Trans>New due date:</Trans>{' '}
              <span className="font-bold text-primary">
                {newDueDate}{' '}
                <small>
                  <Trans>(+15 calendar days)</Trans>
                </small>
              </span>
            </div>
            <div className="mt-3">
              <Label className="control-label">
                <Trans>Justification</Trans>
                <span className="text-danger">*</span>
              </Label>
              <Input
                type="textarea"
                value={justification}
                onChange={e => setJustification(e.target.value)}
                placeholder={t`Provide a detailed justification for your extension request...`}
                {...getTestId('extension-request-modal', 'justification')}
              />
            </div>
          </>
        )}
      </ModalBody>

      <ModalFooter className="justify-content-spaced-between">
        <div>
          {error != null && (
            <span className="text-danger">
              <Icon icon="Warning" className="text-danger" /> {error.message || <Trans>There was an error with your request</Trans>}
            </span>
          )}
        </div>
        <div className="flex gap-1">
          {/* After any result, just show a Close button */}
          {result ? (
            <Button color={isSuccess ? 'primary' : 'secondary'} onClick={handleClose} {...getTestId('extension-request-modal', 'close')}>
              <Trans>Close</Trans>
            </Button>
          ) : (
            <>
              <Button onClick={handleClose} {...getTestId('extension-request-modal', 'cancel')}>
                <Trans>Cancel</Trans>
              </Button>
              <Button onClick={handleConfirmRequest} color="primary" disabled={!justification} {...getTestId('extension-request-modal', 'confirm')}>
                <Trans>Confirm Request</Trans>
              </Button>
            </>
          )}
        </div>
      </ModalFooter>
    </Modal>
  );
};

RequestExtensionModal.PARAMS = PARAMS;

const RequestExtensionButton = ({ rowObject, testIdBase }: { rowObject: UWEEntity; testIdBase?: string }) => {
  const openModal = useOpenModal();

  const extensionsContext = useContext(OcifExtensionsContext);
  const { extensionsThisYear, loading, setFetchExtensions = () => {} } = extensionsContext || {};

  const [status, setStatus] = useState<string>('');

  useEffect(() => {
    setFetchExtensions(true);
  }, [status]);

  if (status === 'success') {
    return null;
  }

  if (extensionsThisYear && extensionsThisYear >= 2) {
    return null;
  }

  if (rowObject?.status?.some(s => s.step === 'report_revision')) {
    return null;
  }

  if (loading) {
    return (
      <Button size="xs" style={{ width: 120 }}>
        <Loader className="white" dots size={20} />
      </Button>
    );
  }

  return (
    <Button size="xs" onClick={() => openModal('RequestExtensionModal', rowObject, setStatus)} {...getTestId(testIdBase)}>
      <Trans>Request Extension</Trans>
    </Button>
  );
};

export { RequestExtensionModal };
export default RequestExtensionButton;
